import {
  GiftsThatGiveBackgroundColors,
  GiftsThatGiveOverlay,
  toGiftsThatGiveBackgroundColor,
} from "@playlive/overlay-core/overlays/gifts-that-give";
import { tiltify } from "@playlive/tiltify-lib";
import { useQuery } from "@tanstack/react-query";
import { deepSignal } from "deepsignal";
import { useEffect, useState } from "preact/hooks";
import CustomizeSection from "@/components/overlays/shared/customize-section.jsx";
import LinkSetup from "@/components/overlays/social/link-setup/link-setup.jsx";
import ScaledOverlayWrapper from "@/components/overlays/wrapper/scaled-overlay-wrapper.jsx";
import ColorPicker from "@/components/pickers/color-picker/color.picker.jsx";
import ColorPickerLabel from "@/components/pickers/color-picker/color-picker-label.jsx";
import useCommon from "@/hooks/useCommon.js";
import { calculateGiftsThatGiveGoal, isYes } from "@/util/common.js";
import { CHARITY } from "@/util/const.js";
import GiftsThatGiveOverlaySetup from "./gifts-that-give.setup.jsx";

const config = deepSignal({
  bgColor: "default-red",
  giftsThatGiveAmount: 0,
});

export default function GiftsThatGiveOverlayPage() {
  const useBetaOverlays = isYes(import.meta.env.VITE_BETA_OVERLAYS || "no");
  const { setConfigState, tiltifyConfig, overlayInfo, overlayURL } = useCommon({
    config,
    forceTiltify: true,
    urlBase: useBetaOverlays ? "overlays/gtg" : "gtg",
  });

  const [campaign, setCampaign] = useState(null);

  const { data: campaigns, isPending: campaignsLoading } = useQuery({
    queryKey: ["tiltify", tiltifyConfig.tiltifyUserID, "getCampaigns"],
    queryFn: async () => {
      if (!tiltifyConfig.tiltifyUserID) {
        return [];
      }

      tiltify.UseProxy();
      try {
        return (await tiltify.GetUserCampaigns(tiltifyConfig.tiltifyUserID)).filter(
          (c) => c.status === "published",
        );
      } catch (err) {
        throw new Error(err.message);
      }
    },
    placeholderData: [],
  });

  if (campaigns.length && !campaignsLoading) {
    if (tiltifyConfig.tiltifyCampaignID) {
      setCampaign(campaigns.find((c) => c.id === tiltifyConfig.tiltifyCampaignID));
    } else {
      setCampaign(campaigns[0]);
    }
  }

  useEffect(() => {
    if (campaign) {
      config.giftsThatGiveAmount = calculateGiftsThatGiveGoal(campaign.goal.value);
    } else {
      config.giftsThatGiveAmount = 0;
    }
  }, [campaign]);

  const giftMilestones = [
    {
      id: 21,
      value: "100.00",
      description: "a shower chair",
      icon: "handheart",
      cover: "cover the cost of",
    },
    {
      id: 101,
      value: "150.00",
      description: "a set of school supplies",
      icon: "paintbrush",
      cover: "cover the cost of",
    },
    {
      id: 5,
      value: "300.00",
      description: "a day of oxygen",
      icon: "kid",
      cover: "provide",
    },
    {
      id: 20,
      value: "1450.00",
      description: "one day outpatient visit",
      icon: "doctor",
      cover: "cover the cost of",
    },
  ];

  function filterColors(color) {
    return GiftsThatGiveBackgroundColors.find((c) => c === color);
  }

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col overflow-x-hidden">
        <div className="example_overlay select-none overflow-x-hidden w-full">
          <ScaledOverlayWrapper allowScaling={false} innerClassName="h-[700px]">
            <div className="mt-24 -mb-5">
              <GiftsThatGiveOverlay
                goalAmount={1000}
                currentAmount={200}
                giftMilestones={giftMilestones}
                backgroundColor={toGiftsThatGiveBackgroundColor(config.bgColor)}
              />
            </div>
          </ScaledOverlayWrapper>
        </div>

        <div>
          <CustomizeSection title="Appearance">
            <ColorPickerLabel color={config.bgColor} />
            <ColorPicker
              fieldName="bgColor"
              currentColor={config.bgColor}
              filterFunction={filterColors}
              onChange={setConfigState}
            />
          </CustomizeSection>
        </div>
      </div>

      <LinkSetup
        constrainWidth={false}
        charityType={CHARITY.TILTIFY}
        childrenSlot="stepTwo"
        childrenSlotPosition="after"
        obsDimensions={overlayInfo.obsDimensions}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
      >
        <GiftsThatGiveOverlaySetup />
      </LinkSetup>
    </div>
  );
}
